<template>

  <Teleport to="title">{{ title }}</Teleport>

  <async-panel :status="initStatus">

    <h2 class="mb-8">{{ title }}</h2>

    <v-form @submit.prevent="submit" novalidate>

      <v-card :title="$t('titles.general')" class="mb-10">
        <template v-slot:text>

          <v-row>

            <v-col cols="12" lg="4">

              <o-autocomplete
                :label="$t('titles.product')"
                :items="productOptions"
                v-model.number="v$.productID.$model"
                :validator="v$.productID"
                :readonly="!canEditLocationProducts || !isNew"
                required
                :autofocus="isNew"
              >
                
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props" :subtitle="$t(`enums.product_types.${item.raw.productType}`) + ' – ' + VerticalRegistry.get(item.raw.verticalType)?.title ?? item.raw.verticalType">
                      <template v-if="canSeeIds" v-slot:append>
                        <span class="text-muted text-caption">{{ item.value }}</span>
                      </template>
                  </v-list-item>
                </template>

                <template v-if="isNew" v-slot:append>
                  <v-menu location="bottom" :close-on-content-click="false">
                    <template v-slot:activator="{ props }">
                      <v-btn v-bind="props" icon>
                        <v-icon :icon="formState.verticalType || formState.productType ? '$filterActive' : '$filter'" />
                        <v-tooltip activator="parent" text="Filter products…" />
                      </v-btn>
                    </template>
                    <v-list open-strategy="single">
                      <v-list-group>
                        <template v-slot:activator="{ props }">
                          <v-list-item v-bind="props" title="Vertical" />
                        </template>
                        <v-list-item>
                          <v-radio-group v-model="formState.verticalType">
                            <v-radio label="Any" :value="null" />
                            <v-radio
                              v-for="option in VerticalRegistry.getOptions()"
                              :label="option.title"
                              :value="option.value"
                            />
                          </v-radio-group>
                        </v-list-item>
                      </v-list-group>
                      <v-list-group>
                        <template v-slot:activator="{ props }">
                          <v-list-item v-bind="props" title="Product Type" />
                        </template>
                        <v-list-item>
                          <v-radio-group v-model="formState.productType">
                            <v-radio label="Any" :value="null" />
                            <v-radio
                              v-for="option in ProductTypeRegistry.getOptions()"
                              :label="option.title"
                              :value="option.value"
                            />
                          </v-radio-group>
                        </v-list-item>
                      </v-list-group>
                    </v-list>
                  </v-menu>
                </template>

              </o-autocomplete>
              
            </v-col>

            <v-col cols="3" lg="2">
              <v-text-field
                v-if="!isNew"
                readonly
                variant="plain"
                :label="$t('titles.vertical')"
                :model-value="VerticalRegistry.getTitle(product.verticalType)"
              />
            </v-col>

            <v-col cols="3" lg="2">
              <v-text-field
                v-if="!isNew"
                readonly
                variant="plain"
                :label="$t('titles.product_type')"
                :model-value="ProductTypeRegistry.getTitle(product.productType)"
              />
            </v-col>

            <v-col cols="3" lg="2">
              <v-checkbox
                v-if="product?.verticalType === 'transient'"
                :label="$t('titles.early_bird_rate')"
                v-model="formState.earlyBird"
                :disabled="!canEditLocationProducts"
              />
            </v-col>

            <v-col cols="3" lg="2">
              <v-checkbox
                v-if="product?.verticalType === 'transient'"
                :label="$t('titles.in_out')"
                v-model="formState.inOut"
                :disabled="!canEditLocationProducts"
              />
            </v-col>

          </v-row>

          <v-row>

            <v-col cols="12" sm="6">
              <o-select
                :label="$t('titles.parcs')"
                v-model.number="formState.parcsID"
                :items="parcsOptions"
                :clearable="canEditLocationProducts"
                :readonly="!canEditLocationProducts"
              />
            </v-col>

          </v-row>

          <template v-if="showEventOverrideFields">

            <v-row>

              <v-col>
                <o-select
                  :label="$t('titles.entry_hours_before_event')"
                  v-model.number="v$.entryHoursBeforeEvent.$model"
                  :validator="v$.entryHoursBeforeEvent"
                  :items="timeOffsetOptions"
                  required
                  :readonly="!canEditLocationProducts"
                />
              </v-col>

              <v-col>
                <o-select
                  :label="$t('title.exit_hours_after_event')"
                  v-model.number="v$.exitHoursAfterEvent.$model"
                  :validator="v$.exitHoursAfterEvent"
                  :items="timeOffsetOptions"
                  required
                  :readonly="!canEditLocationProducts"
                />
              </v-col>

            </v-row>

          </template>

          <template v-if="showTimeOverrideFields">

            <v-row>

              <v-col>
                <o-text-field
                  :label="$t('titles.first_available_start_time')"
                  type="time"
                  v-model="v$.firstStartTime.$model"
                  :validator="v$.firstStartTime"
                  required
                  :readonly="!canEditLocationProducts"
                  :clearable="canEditLocationProducts"
                />
              </v-col>

              <v-col>
                <o-text-field
                  :label="$t('titles.last_available_start_time')"
                  type="time"
                  v-model="v$.lastStartTime.$model"
                  :validator="v$.lastStartTime"
                  required
                  :readonly="!canEditLocationProducts"
                  :clearable="canEditLocationProducts"
                />
              </v-col>

              <v-col>
                <o-text-field
                  :label="$t('titles.end_time')"
                  type="time"
                  v-model="v$.endTime.$model"
                  :validator="v$.endTime"
                  required
                  :readonly="!canEditLocationProducts"
                  :clearable="canEditLocationProducts"
                />
              </v-col>

            </v-row>

          </template>

        </template>
      </v-card>

      <v-card :title="$t('titles.channels')" class="mb-10">
        <template v-slot:text>

          <v-table density="compact">
            <thead>
              <tr>
                <th scope="col">{{ $t('titles.channel') }}</th>
                <th v-if="canSeeIds" scope="col">{{ $t('titles.ocra_channel_facility_id') }}</th>
                <th scope="col">{{ $t('titles.channel_side_facility_id') }}</th>
                <th scope="col">{{ $t('titles.enabled') }}</th>
                <th scope="col">{{ $t('titles.do_not_send_rates_to_channel') }}</th>
                <th scope="col" style="width: 200px">{{ $t('titles.discounts') }}</th>
              </tr>
            </thead>
            <tbody>
              <lpcf-row
                v-for="channelFacility in channelFacilities"
                :key="channelFacility.ID"
                :channel-facility="channelFacility"
                :location-id="location.ID"
                :location-product-id="locationProductID"
                :readonly="!canEditLocationProducts"
                v-model:channel-facility-ids="formState.channelFacilityIDs"
                v-model:dont-send-rates-for-channel-facility-ids="formState.dontSendRatesForChannelFacilityIDs"
              />
            </tbody>
          </v-table>

          <v-alert v-if="showParkWhizLpcfNotice" color="info" class="mt-4" icon="$alert">
            You're linking a new ParkWhiz facility to this product, or you've toggled the "Do Not Send Rates to Channel" checkbox for an already-linked ParkWhiz facility.
            If there are any other Transient products at this location which are linked to the same ParkWhiz facility, their "Do Not Send Rates to Channel" checkboxes will be updated to match this one.
          </v-alert>

        </template>
      </v-card>

      <v-card :title="$t('titles.default_rates')" class="mb-10">
        <template v-slot:text>

          <v-alert v-if="!formState.productID" color="info">
            <p>{{ $t('alerts.please_select_a_product') }}</p>
          </v-alert>

          <template v-if="formState.productID">

            <v-alert
              v-if="product.verticalType === 'spothero_iq'"
              color="info"
              :text="$t('alerts.spothero_iq_products_do_not_have_default_rates')"
            />

            <v-alert
              v-if="product.verticalType === 'parcs'"
              color="info"
              :text="$t('alerts.parcs_products_do_not_have_default_rates')"
            />

            <template v-if="product.verticalType === 'event'">

              <template v-if="isNew">

                <o-text-field
                  :label="$t('titles.default_inventory')"
                  type="number"
                  min="1"
                  step="1"
                  v-model.number="v$.defaultInventory.$model"
                  :validator="v$.defaultInventory"
                  required
                />

                <o-text-field
                  :label="$t('titles.default_cost')"
                  type="number"
                  v-model.number="v$.defaultPrice.$model"
                  :validator="v$.defaultPrice"
                  min="0.01"
                  step="0.01"
                  prefix="$"
                  required
                />

              </template>

              <template v-if="!isNew">

                <v-alert color="info">
                  <p>{{ $t('alerts.defaults_for_event_products_can_only_be_set_at_creation_time') }}</p>
                </v-alert>

              </template>

            </template>

            <div v-if="['airport', 'spothero_in_out', 'transient', 'monthly', 'on_demand'].includes(product.verticalType)" style="max-width: 800px">
              <RateSet
                v-model="formState.rates"
                :location="location"
                :tiered="VerticalRegistry.get(product.verticalType)?.supports(['tieredRates'])"
                :readonly="!canEditLocationProducts"
                :only-channel-facility-ids="formState.channelFacilityIDs"
                :max-tier-duration-in-hours="showTimeOverrideFields ? maxTierDurationInHours : null"
              />
            </div>

          </template>

        </template>
      </v-card>

      <v-btn
        v-if="canEditLocationProducts"
        type="submit"
        prepend-icon="$confirm"
        :loading="submitting"
        :disabled="submitting"
      >{{ $t('actions.save') }}</v-btn>

    </v-form>

  </async-panel>

</template>

<script setup>

import { computed, inject, onBeforeMount, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

// components
import AsyncPanel from '../../../AsyncPanel.vue'
import LpcfRow from './LpcfRow.vue'
import RateSet from '../../../RateSet.vue'

// models
import { ChannelFacility } from '../../../../models/ChannelFacility'
import { DefaultRate } from '../../../../models/DefaultRate'
import Location from '../../../../models/Location'
import LocationProduct from '../../../../models/LocationProduct'
import Parcs from '../../../../models/Parcs'
import Product from '../../../../models/Product'
import SpotHeroFlexDiscount from '../../../../models/SpotHeroFlexDiscount'

// validation
import useVuelidate from '@vuelidate/core'
import { required, minValue } from '../../../../utils/i18n-validators'
import { helpers, requiredIf } from '@vuelidate/validators'
import dollarAmount from '../../../../rules/dollarAmount'

// enums
import { ParcsCompanies } from '../../../../enums'

// misc
import asyncStatus from '../../../../utils/asyncStatus'
import { buildRateSetFormState, saveRateSetFormState } from '../../../../utils/rateSetUtils'
import getEnumLabel from '../../../../utils/getEnumLabel'
import NotificationService from '../../../../services/NotificationService'
import RatesApi from '../../../../services/api-clients/RatesApi'
import timeOffsetOptions from '../../../../utils/timeOffsetOptions'
import ProductTypeRegistry from '../../../../product_types/ProductTypeRegistry'
import VerticalRegistry from '../../../../verticals/VerticalRegistry'
import { useProductMaxDuration } from '../../../../utils/useProductMaxDuration'

const props = defineProps({

  location: {
    type: Location,
    required: true,
  },

  locationProductID: {
    type: Number,
  },

})

const router = useRouter()
const isNew = !props.locationProductID
const { t } = useI18n()

const $can = inject('$can')
const canSeeIds = $can('seeids')
const canEditLocationProducts = $can('locationProducts.write')

const title = computed(() => {
  return isNew ? t('titles.new_product') : (product.value ? product.value.name : '(unknown)')
})

/**
 * Dropdown options
 */

const productOptions = computed(() => {

  const query = Product.query()
    .where('organizationID', props.location.organizationID)
    .orderBy((product) => product.name.toUpperCase())

  // apply filters

  if (formState.productType) {
    query.where('productType', formState.productType)
  }

  if (formState.verticalType) {
    query.where('verticalType', formState.verticalType)
  }

  const products = query.get()
  const options = []

  products.forEach((product) => {
    options.push({
      value: product.ID,
      title: product.name,
      productType: product.productType,
      verticalType: product.verticalType,
    })
  })

  return options

})

const parcsOptions = computed(() => {
  return Parcs.query().where('locationID', props.location.ID).get().map((parcs) => {
    return {
      value: parcs.ID,
      title: getEnumLabel(parcs.companyName, ParcsCompanies) + ' (' + parcs.externalID + ')'
    }
  })
})

const channelFacilities = computed(() => {

  const query = ChannelFacility.query()
    .where('facilityID', props.location.facilityID)
    .orderBy('channel')
    .orderBy('externalFacilityID')

  // If an event Product is selected, limit the selectable ChannelFacilities to SpotHero and ParkWhiz
  if (eventProductIsSelected.value) {
    query.where('channel', (channel) => ['parkwhiz', 'spothero'].includes(channel))
  } else if (spotHeroInOutProductIsSelected.value) {
    query.where('channel', 'spothero')
  }
  
  return query.get()

})

/**
 * Form state and rules
 */

const formState = reactive({
  productID: null,
  productType: null,
  earlyBird: false,
  inOut: false,
  verticalType: null,
  parcsID: null,
  entryHoursBeforeEvent: null,
  exitHoursAfterEvent: null,
  firstStartTime: null,
  lastStartTime: null,
  endTime: null,
  channelFacilityIDs: [],
  dontSendRatesForChannelFacilityIDs: [],
  rates: {
    // TODO: having this nested like this is annoying. we're only doing it so we can pass it to the v-model of NewRateSet. can we fix this?
    defaultRates: [],
  },
  defaultInventory: null,
  defaultPrice: null,
})

const product = computed(() => {
  return formState.productID ? Product.find(formState.productID) : null
})

const eventProductIsSelected = computed(() => {
  return product.value && product.value.verticalType === 'event'
})

const spotHeroInOutProductIsSelected = computed(() => {
  return product.value && product.value.verticalType === 'spothero_in_out'
})

const showDefaultEventFields = computed(() => {
  return isNew && eventProductIsSelected.value
})

const showTimeOverrideFields = computed(() => {
  return product.value && ['transient', 'on_demand'].includes(product.value.verticalType)
})

const showEventOverrideFields = computed(() => {
  return product.value && product.value.verticalType === 'event'
})

const formRules = {
  productID: { required },
  entryHoursBeforeEvent: {
    requiredIf: helpers.withMessage(t('validations.required'), requiredIf(showEventOverrideFields)),
  },
  exitHoursAfterEvent: {
    requiredIf: helpers.withMessage(t('validations.required'), requiredIf(showEventOverrideFields)),
  },
  firstStartTime: {},
  lastStartTime: {},
  endTime: {},
  channelFacilityIDs: {},
  defaultInventory: {
    requiredIf: helpers.withMessage(t('validations.required'), requiredIf(showDefaultEventFields)),
    minValue: minValue(1),
  },
  defaultPrice: {
    requiredIf: helpers.withMessage(t('validations.required'), requiredIf(showDefaultEventFields)),
    minValue: minValue(0.01),
    dollarAmount: helpers.withMessage(t('validations.dollar_amount'), dollarAmount),
  }
}

const v$ = useVuelidate(formRules, formState)

// If the "Filter Products by Vertical" dropdown is changed to a vertical
// that's different than the selected product's vertical, clear the selected
// product
watch(() => formState.verticalType, (newVerticalType) => {
  if (
    newVerticalType &&
    product.value &&
    product.value.verticalType !== newVerticalType
  ) {
    formState.productID = null
  }
})

// If the "Filter Products by Product Type" dropdown is changed to a vertical
// that's different than the selected product's vertical, clear the selected
// product
watch(() => formState.productType, (newProductType) => {
  if (
    newProductType &&
    product.value &&
    product.value.productType !== newProductType
  ) {
    formState.productID = null
  }
})

watch(product, (newProduct, oldProduct) => {

  if (!isNew) {
    return
  }
  
  const oldVertical = oldProduct ? oldProduct.verticalType : null
  const newVertical = newProduct ? newProduct.verticalType : null

  if (newVertical === 'event') {
    formState.entryHoursBeforeEvent = newProduct.entryHoursBeforeEvent
    formState.exitHoursAfterEvent = newProduct.exitHoursAfterEvent
    // Remove any previously enabled ChannelFacilities which are no longer valid
    const validChannelFacilityIDs = channelFacilities.value.map((channelFacility) => channelFacility.ID)
    formState.channelFacilityIDs = formState.channelFacilityIDs.filter((channelFacilityID) => validChannelFacilityIDs.includes(channelFacilityID))
  } else {
    formState.entryHoursBeforeEvent = null
    formState.exitHoursAfterEvent = null
  }

  if (newVertical === 'transient') {
    formState.firstStartTime = newProduct.firstStartTime
    formState.lastStartTime = newProduct.lastStartTime
    formState.endTime = newProduct.endTime
  } else {
    formState.firstStartTime = null
    formState.lastStartTime = null
    formState.endTime = null
  }

  // If the user changes the product from a vertical that expects one type of
  // rate, to a vertical that expects a different type of rate, then the
  // contents of formState.rates.defaultRates will no longer make sense. Clear
  // and repopulate it.

  const oldRateType = getRateTypeForVertical(oldVertical)
  const newRateType = getRateTypeForVertical(newVertical)

  if (newRateType !== oldRateType) {

    let newDefaultRates = []

    switch (newRateType) {

      case 'normal':
        newDefaultRates.push({
          ID: null,
          defaultCost: null,
          perQuantity: null,
          perUnit: null,
          channelDefaultRates: [],
        })
        break

      case 'tiered':
        newDefaultRates.push({
          ID: null,
          defaultCost: null,
          perQuantity: 1,
          perUnit: 'hour',
          channelDefaultRates: [],
        })
        break

      case 'none':
        // do nothing
        break

    }

    formState.rates.defaultRates = buildRateSetFormState(newDefaultRates, channelFacilities.value)

  }

})

const { getComputedsForStrings } = useProductMaxDuration()

const { maxTierDurationInHours } = getComputedsForStrings(
  computed(() => formState.firstStartTime),
  computed(() => formState.lastStartTime),
  computed(() => formState.endTime),
)

function getRateTypeForVertical(vertical) {

  switch (vertical) {
    case 'airport':
    case 'monthly':
      return 'normal'
      break
    case 'transient':
    case 'spothero_in_out':
      return 'tiered'
      break
    case 'event':
    case 'spothero_iq':
      return 'empty'
      break
    default:
      return 'na'
      break
  }

}

const locationProduct = ref(null)

/**
 * Initialization
 */

const initStatus = asyncStatus()

onBeforeMount(() => {

  initStatus.setBusy()

  const promises = []

  // ChannelFacilities are needed by both the Channels and Default Rates tabs,
  // so load them here in the parent component.
  promises.push(ChannelFacility.fetchByLocationID(props.location.ID))
  promises.push(Parcs.fetchByLocationID(props.location.ID))

  // If we're editing an existing LP, then load it from the API
  if (props.locationProductID) {

    promises.push(
      LocationProduct.fetch(props.location.ID, props.locationProductID)
    )

    promises.push(
      // This retrieves rates for *all* LPs at this location. It also retrieves
      // both default and spot rates. Unforch, the API doesn't provide any way
      // to filter the result set by these parameters.
      RatesApi.fetchByLocationID(props.location.ID, {
        start: '1990-01-01',
        end: '1990-01-01',
      })
    )

    promises.push(SpotHeroFlexDiscount.fetchByLocationProductId(props.location.ID, props.locationProductID))

  }

  Promise.all(promises)
    .then(() => {
      locationProduct.value = LocationProduct.query().where('ID', props.locationProductID).with('product').first()
      populateForm()
      initStatus.setReady()
    })
    .catch((error) => {
      console.error(error)
      initStatus.setError(error)
      NotificationService.error(t('alerts.couldnt_load_data'))
    })

})

// keep track of which DefaultRates existed at init time, so that submit() can delete any that were removed
let originalDefaultRateIDs = []

function populateForm() {

  if (!isNew) {

    formState.productID = locationProduct.value.productID
    formState.earlyBird = locationProduct.value.earlyBird
    formState.inOut = locationProduct.value.inOut
    formState.parcsID = locationProduct.value.parcsID
    formState.verticalType = locationProduct.value.product.verticalType
    formState.productType = locationProduct.value.product.productType
    formState.entryHoursBeforeEvent = locationProduct.value.entryHoursBeforeEvent
    formState.exitHoursAfterEvent = locationProduct.value.exitHoursAfterEvent
    formState.firstStartTime = locationProduct.value.firstStartTime
    formState.lastStartTime = locationProduct.value.lastStartTime
    formState.endTime = locationProduct.value.endTime
    formState.channelFacilityIDs = locationProduct.value.channelFacilityIDs
    formState.dontSendRatesForChannelFacilityIDs = channelFacilities.value
      .map(cf => cf.ID)
      .filter(cfID => locationProduct.value.channelFacilityIDs.includes(cfID) && !locationProduct.value.sendRatesForChannelFacilityIDs.includes(cfID))

  }

  /**
   * Populate the fields for the Default Rates panel
   */

  let defaultRates = []

  if (isNew) {
    defaultRates = [new DefaultRate()]
  } else {
    defaultRates = DefaultRate.query().with(['channelDefaultRates']).where('locationProductID', props.locationProductID).get()
    originalDefaultRateIDs = defaultRates.map((defaultRate) => defaultRate.ID)
  }

  formState.rates.defaultRates = buildRateSetFormState(defaultRates, channelFacilities.value)

}

/**
 * Submission
 */

const submitting = ref(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid || !canEditLocationProducts) {
    return
  }

  submitting.value = true

  /**
   * First save the location product itself
   */

  let promise

  const payload = {
    earlyBird: formState.earlyBird,
    inOut: formState.inOut,
    parcsID: formState.parcsID ? formState.parcsID : null,
    channelFacilityIDs: formState.channelFacilityIDs,
    sendRatesForChannelFacilityIDs: formState.channelFacilityIDs.filter(cfID => !formState.dontSendRatesForChannelFacilityIDs.includes(cfID)),
  }

  if (showEventOverrideFields.value) {
    payload.entryHoursBeforeEvent = formState.entryHoursBeforeEvent
    payload.exitHoursAfterEvent = formState.exitHoursAfterEvent
  }

  if (showTimeOverrideFields.value) {
    payload.firstStartTime = normalizeTime(formState.firstStartTime)
    payload.lastStartTime = normalizeTime(formState.lastStartTime)
    payload.endTime = normalizeTime(formState.endTime)
  }

  if (showDefaultEventFields.value) {
    payload.defaultInventory = formState.defaultInventory
    payload.defaultPrice = formState.defaultPrice
  }

  if (isNew) {
    payload.productID = formState.productID
    promise = LocationProduct.saveNew(props.location.ID, payload)
  } else {
    promise = LocationProduct.saveExisting(props.location.ID, props.locationProductID, payload)
  }

  promise
    .then((result) => {

      // The LocationProduct has been saved. Now save the default rates.
      const locationProductID = result.response.data.ID

      if (product.value.verticalType === 'event') {
        
        // Event LPs don't have default rates, so there are none for us to save.
        // (formState.rates.defaultRates should be empty in this case anyway,
        // but we'll also check here just in case.)
        
        return Promise.resolve()

      } else if (product.value.verticalType === 'spothero_iq') {
        
        // Even though SpotHero IQ LPs don't have default rates as such, we
        // still make a call to the Create Default Rate endpoint. This will
        // trigger the rates workers to fetch the facility's IQ configuration
        // from SpotHero, and write it to channel_default_rate.channel_rate_data.
        // See here: https://trello.com/c/P3wKZqwC

        if (isNew) {
          return DefaultRate.bulkCreate(props.location.ID, [{
            locationProductID: locationProductID,
            defaultCost: 888.88, // this is an arbitrary placeholder value (since this column must have *some* value)
            perQuantity: null,
            perUnit: null,
            channelDefaultRates: [],
          }])
        } else {
          return Promise.resolve()
        }

      } else if (['airport', 'spothero_in_out', 'transient', 'monthly', 'on_demand'].includes(product.value.verticalType)) {

        return saveRateSetFormState(
          props.location.ID,
          locationProductID,
          formState.rates.defaultRates,
          originalDefaultRateIDs,
          formState.channelFacilityIDs,
          true,
        )

      }

    })
    .then(() => {

      NotificationService.success(t('alerts.product_saved'))
      router.push({
        name: 'locationProductIndex',
        params: {
          locationID: props.location.ID,
        }
      })
    })
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_save_product'))
    })
    .finally(() => {
      submitting.value = false
    })

}

/**
 * Miscellaneous
 */

/**
 * The Product model (and the API) represent the time fields in hh:mm:ss
 * format, but selecting a time value on the form produces a value in
 * hh:mm format (in some browsers, at least). Fix that.
 * 
 * Also convert empty strings to nulls.
 */
function normalizeTime(time) {
  if (!time) {
    return null
  }
  if (/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(time)) {
    time += ':00'
  }
  return time
}

const parkWhizChannelFacilityIDs = computed(() => {
  return channelFacilities.value.filter(cf => cf.channel === 'parkwhiz').map(cf => cf.ID)
})

const showParkWhizLpcfNotice = computed(() => {

  if (formState.verticalType !== 'transient') {
    return false
  }

  const parkWhizChannelFacilityIDsThatLpcfsAlreadyExistFor = parkWhizChannelFacilityIDs.value
    .filter(cfID => isNew ? false : locationProduct.value.channelFacilityIDs.includes(cfID))

  const parkWhizChannelFacilityIDsThatLpcfsWillBeCreatedFor = formState.channelFacilityIDs.filter((cfID) => {
    if (!parkWhizChannelFacilityIDs.value.includes(cfID)) {
      return false // this is not a ParkWhiz CF
    }
    return !parkWhizChannelFacilityIDsThatLpcfsAlreadyExistFor.includes(cfID)
  })

  const parkWhizChannelFacilityIDsThatLpcfsWillBeUpdatedFor = parkWhizChannelFacilityIDs.value
    .filter((cfID) => {
      if (isNew) {
        return false
      }
      const lpcfExists = parkWhizChannelFacilityIDsThatLpcfsAlreadyExistFor.includes(cfID)
      let sendRatesCheckboxHasBeenChanged = false
      if (formState.dontSendRatesForChannelFacilityIDs.includes(cfID)) {
        sendRatesCheckboxHasBeenChanged = locationProduct.value.sendRatesForChannelFacilityIDs.includes(cfID)
      } else {
        sendRatesCheckboxHasBeenChanged = !locationProduct.value.sendRatesForChannelFacilityIDs.includes(cfID)
      }
      return lpcfExists && sendRatesCheckboxHasBeenChanged
    })

  const show = parkWhizChannelFacilityIDsThatLpcfsWillBeCreatedFor.length || parkWhizChannelFacilityIDsThatLpcfsWillBeUpdatedFor.length
  return show

})

</script>
